import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import { Box, FormHelperText, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

type DateRange = {
  from: string | null;
  to: string | null;
};

export type DateRangePickerProps = {
  value: DateRange;
  onChange: (value: DateRange) => void;
  label?: string;
  helperText?: string;
  direction?: 'row' | 'column';
  outputFormat?: 'iso' | 'date';
  minDate?: Dayjs;
  maxDate?: Dayjs;
};

export const DateRangePicker = (props: DateRangePickerProps) => {
  const {
    value,
    onChange,
    label,
    helperText,
    direction = 'row',
    outputFormat = 'date',
    minDate,
    maxDate,
  } = props;

  const fromDayjs = value.from ? dayjs(value.from) : null;
  const toDayjs = value.to ? dayjs(value.to) : null;

  const formatDate = (date: Dayjs) => {
    if (outputFormat === 'iso') {
      const now = dayjs();
      return date
        .hour(now.hour())
        .minute(now.minute())
        .second(now.second())
        .millisecond(now.millisecond())
        .toISOString();
    }
    return date.format('YYYY-MM-DD');
  };

  const handleFromChange = (from: Dayjs | null) => {
    onChange({ ...value, from: from ? formatDate(from) : null });
  };

  const handleToChange = (to: Dayjs | null) => {
    onChange({ ...value, to: to ? formatDate(to) : null });
  };

  return (
    <Box>
      {label && (
        <Typography variant="body2" mb={1} color="textPrimary" fontWeight={400}>
          {label}
        </Typography>
      )}
      <Stack
        direction={direction}
        spacing={1}
        alignItems={direction === 'row' ? 'center' : 'stretch'}
      >
        <DatePicker
          value={fromDayjs}
          onChange={handleFromChange}
          minDate={minDate}
          maxDate={toDayjs ?? maxDate}
          localeText={{
            fieldDayPlaceholder: () => '01',
            fieldMonthPlaceholder: () => '01',
            fieldYearPlaceholder: () => '1970',
          }}
          slotProps={{
            textField: { size: 'small', fullWidth: true, variant: 'outlined' },
          }}
        />
        {direction === 'row' && <Typography>-</Typography>}
        <DatePicker
          value={toDayjs}
          onChange={handleToChange}
          minDate={fromDayjs ?? minDate}
          maxDate={maxDate}
          localeText={{
            fieldDayPlaceholder: () => '01',
            fieldMonthPlaceholder: () => '01',
            fieldYearPlaceholder: () => '1970',
          }}
          slotProps={{
            textField: { size: 'small', fullWidth: true, variant: 'outlined' },
          }}
        />
      </Stack>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </Box>
  );
};
